import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Plug } from 'lucide-react';
import { integrationsApi } from '@/api/integrations';
import { unwrapError } from '@/api/client';
import type { IntegrationStatus } from '@/api/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useWorkspace } from '@/workspace/WorkspaceContext';
import { IntegrationInstanceCard } from '@/components/integrations/IntegrationInstanceCard';
import { AddInstanceDialog } from '@/components/integrations/AddInstanceDialog';
import { EditInstanceDialog } from '@/components/integrations/EditInstanceDialog';

export function Integrations(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentBotId } = useWorkspace();
  const [addOpen, setAddOpen] = React.useState(false);
  const [editInstance, setEditInstance] = React.useState<IntegrationStatus | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [filter, setFilter] = React.useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['integrations', currentBotId],
    queryFn: () => integrationsApi.list(),
    enabled: !!currentBotId,
    staleTime: 15_000,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['integrations', currentBotId] });
    void queryClient.invalidateQueries({ queryKey: ['integration-types', currentBotId] });
    void queryClient.invalidateQueries({ queryKey: ['bot', 'system', currentBotId] });
  };

  const handleEdit = (instance: IntegrationStatus): void => {
    setEditInstance(instance);
    setEditOpen(true);
  };

  const items = React.useMemo(() => {
    const all = data?.items ?? [];
    const q = filter.trim().toLowerCase();
    if (!q) return all;
    return all.filter(
      (i) =>
        i.label.toLowerCase().includes(q) ||
        i.type.toLowerCase().includes(q) ||
        (i.description ?? '').toLowerCase().includes(q),
    );
  }, [data, filter]);

  if (!currentBotId) {
    return (
      <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
        {t('integrations.noBotSelected')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('integrations.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('integrations.subtitle')}</p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="gap-1.5">
          <Plus className="h-4 w-4" />
          {t('integrations.addButton')}
        </Button>
      </header>

      {(data?.items?.length ?? 0) > 0 && (
        <div className="flex items-center gap-2">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('integrations.filterPlaceholder')}
            className="max-w-md"
          />
          <span className="text-xs text-muted-foreground">
            {items.length} / {data?.items?.length ?? 0}
          </span>
        </div>
      )}

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('common.loading')}
          </CardContent>
        </Card>
      ) : error ? (
        <p className="text-sm text-destructive">{unwrapError(error)}</p>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 p-12 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Plug className="h-8 w-8 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium">
                {filter
                  ? t('integrations.noResults')
                  : t('integrations.emptyTitle')}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {filter
                  ? t('integrations.noResultsHint')
                  : t('integrations.emptySubtitle')}
              </p>
            </div>
            {!filter && (
              <Button onClick={() => setAddOpen(true)} variant="outline" className="gap-1.5">
                <Plus className="h-4 w-4" />
                {t('integrations.addFirstButton')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((instance) => (
            <IntegrationInstanceCard
              key={instance.instanceId}
              instance={instance}
              onEdit={() => handleEdit(instance)}
              onMutated={invalidate}
            />
          ))}
        </div>
      )}

      <AddInstanceDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={invalidate}
      />

      <EditInstanceDialog
        instance={editInstance}
        open={editOpen}
        onOpenChange={setEditOpen}
        onSaved={invalidate}
      />
    </div>
  );
}
